import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import apiClient, { formatApiError, BACKEND_URL } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { FloppyDisk, UploadSimple, LockSimple, PaintBrushBroad, Image as ImageIcon } from "@phosphor-icons/react";

const FONTS = [
  { value: "modern", label: "Modern", note: "Clean sans-serif", css: "'Helvetica Neue', Arial, sans-serif" },
  { value: "classic", label: "Classic", note: "Elegant serif", css: "Georgia, 'Times New Roman', serif" },
  { value: "mono", label: "Mono", note: "Technical monospace", css: "'Courier New', monospace" },
];

function ColorField({ label, value, onChange, testId }) {
  const v = value || "#000000";
  return (
    <div>
      <label className="font-mono-plex text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</label>
      <div className="mt-2 flex items-center gap-3">
        <input data-testid={`${testId}-picker`} type="color" value={v} onChange={(e) => onChange(e.target.value)}
          className="h-11 w-14 rounded-md border border-slate-300 bg-white cursor-pointer p-1" />
        <input data-testid={`${testId}-hex`} value={v} onChange={(e) => onChange(e.target.value)}
          className="w-32 h-11 px-3 rounded-md border border-slate-300 bg-white text-sm font-mono-plex uppercase focus:ring-2 focus:ring-slate-900 focus:outline-none" />
      </div>
    </div>
  );
}

export default function BrandingPage() {
  const { user, company, refreshCompany } = useAuth();
  const isAdmin = user?.role === "admin";
  const fileRef = useRef(null);
  const [primary, setPrimary] = useState("#0F172A");
  const [accent, setAccent] = useState("#2563EB");
  const [font, setFont] = useState("modern");
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (company) {
      setPrimary(company.brand_primary_color || "#0F172A");
      setAccent(company.brand_accent_color || "#2563EB");
      setFont(company.brand_font || "modern");
      setLogoUrl(company.logo_url || "");
    }
  }, [company]);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiClient.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(r.data.logo_url);
      await refreshCompany();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/company", { brand_primary_color: primary, brand_accent_color: accent, brand_font: font });
      await refreshCompany();
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save branding");
    } finally { setSaving(false); }
  };

  const fontCss = FONTS.find((f) => f.value === font)?.css;
  const fullLogo = logoUrl ? `${BACKEND_URL}${logoUrl}` : null;

  return (
    <div className="max-w-4xl space-y-6" data-testid="branding-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Proposal Branding</h1>
          <p className="text-slate-500 mt-2">Your logo, colors and fonts are applied to every client-ready proposal Proposal Forensics generates.</p>
        </div>
        {isAdmin && <button data-testid="save-branding-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-60"><FloppyDisk size={17} weight="bold" /> {saving ? "Saving…" : "Save Branding"}</button>}
      </div>

      {!isAdmin && <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3" data-testid="branding-readonly"><LockSimple size={16} weight="bold" /> Read-only. Only an admin can edit branding.</div>}

      <fieldset disabled={!isAdmin} className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-5"><ImageIcon size={18} weight="bold" /><h2 className="font-display font-bold text-lg text-[#0F172A]">Company Logo</h2></div>
            <div className="flex items-center justify-center h-40 rounded-md border-2 border-dashed border-slate-200 bg-slate-50 overflow-hidden">
              {fullLogo
                ? <img data-testid="branding-logo-preview" src={fullLogo} alt="Company logo" className="max-h-32 max-w-[80%] object-contain" />
                : <span className="text-sm text-slate-400">No logo uploaded yet</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onFile} className="hidden" data-testid="branding-logo-input" />
            <button data-testid="branding-upload-btn" onClick={onPickFile} disabled={uploading}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">
              <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : fullLogo ? "Replace Logo" : "Upload Logo"}
            </button>
            <p className="text-xs text-slate-400 mt-2">PNG, JPG, WEBP or SVG · up to 5 MB · transparent PNG looks best.</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div className="flex items-center gap-2"><PaintBrushBroad size={18} weight="bold" /><h2 className="font-display font-bold text-lg text-[#0F172A]">Colors &amp; Font</h2></div>
            <ColorField label="Primary Color" value={primary} onChange={setPrimary} testId="brand-primary" />
            <ColorField label="Accent Color" value={accent} onChange={setAccent} testId="brand-accent" />
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Proposal Font</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {FONTS.map((f) => (
                  <button key={f.value} type="button" data-testid={`brand-font-${f.value}`} onClick={() => setFont(f.value)}
                    className={`rounded-md border p-3 text-left transition-colors ${font === f.value ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
                    <div className="text-base text-[#0F172A]" style={{ fontFamily: f.css }}>Aa</div>
                    <div className="text-xs font-semibold text-[#0F172A] mt-1">{f.label}</div>
                    <div className="text-[10px] text-slate-400">{f.note}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-display font-bold text-lg text-[#0F172A] mb-4">Proposal Cover Preview</h2>
          <div className="rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="branding-preview" style={{ fontFamily: fontCss }}>
            <div className="h-1.5" style={{ backgroundColor: accent }} />
            <div className="p-7">
              {fullLogo && <img src={fullLogo} alt="logo" className="max-h-14 max-w-[55%] object-contain mb-5" />}
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accent }}>{company?.company_name || "Your Company"}</div>
              <div className="text-2xl font-bold mt-2" style={{ color: primary }}>Whole-Home Automation Proposal</div>
              <div className="text-sm text-slate-500 mt-3">Prepared for The Henderson Residence</div>
              <div className="text-sm text-slate-500">Prepared by {user?.name || "Your Team"}</div>
              <div className="h-px my-6" style={{ backgroundColor: accent, opacity: 0.4 }} />
              <div className="text-sm font-semibold" style={{ color: primary }}>Scope of Work</div>
              <div className="h-0.5 w-24 mt-1.5 mb-3" style={{ backgroundColor: accent }} />
              <ul className="text-sm text-slate-600 space-y-1.5">
                <li>• Lutron lighting &amp; motorized shading across 14 zones</li>
                <li>• Control4 whole-home automation with 4 touch panels</li>
                <li>• Structured network with enterprise Wi-Fi coverage</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">This is a live preview. Your real generated proposals use these exact colors, logo and font.</p>
        </div>
      </fieldset>
    </div>
  );
}
